/*
 * SPDX-FileCopyrightText: 2022-2026 CERN.
 * SPDX-License-Identifier: MIT
 */

import { i18next } from "@translations/invenio_rdm_records/i18next";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "semantic-ui-react";
import { changeSelectedCommunity } from "../../state/actions";
import { CommunitySelectionModal } from "../../components/CommunitySelectionModal";
import { PublishButton } from "./PublishButton";
import { SubmitReviewButton } from "./SubmitReviewButton";

class SubmitReviewOrPublishComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalOpen: false,
    };
  }

  doiReservationCheck = (
    isDOIRequired,
    noINeedDOI,
    formik,
    errorMessage,
    errorType
  ) => {
    // Check for explicit DOI reservation via the "GET DOI button" only when DOI is
    // optional in the instance's settings. If it is required, backend will automatically
    // mint one even if it was not explicitly reserved
    const shouldCheckForExplicitDOIReservation =
      isDOIRequired !== undefined && // isDOIRequired is undefined when no value was provided from Invenio-app-rdm
      !isDOIRequired &&
      noINeedDOI &&
      // Check if there is no DOI reserved yet. We check for doi explicitly as the pids object
      // might contain other identifiers e.g. oai
      Object.keys(formik?.values?.pids?.doi || {}).length === 0;
    if (shouldCheckForExplicitDOIReservation) {
      const errors = {
        pids: {
          doi: i18next.t(errorMessage),
        },
      };
      formik.setErrors(errors);
      const { raiseDOINeededButNotReserved } = this.props;
      raiseDOINeededButNotReserved(formik?.values, errors, errorType);
    }
    return shouldCheckForExplicitDOIReservation;
  };

  render() {
    const {
      community,
      changeSelectedCommunityFn,
      showChangeCommunityButton,
      showDirectPublishButton,
      showSubmitForReviewButton,
      record,
      gprUrl,
      ...ui
    } = this.props;
    const { modalOpen } = this.state;
    let result;

    if (showSubmitForReviewButton) {
      result = (
        <SubmitReviewButton
          directPublish={showDirectPublishButton}
          {...ui}
          fluid
          className="mb-10"
          record={record}
          gprUrl={gprUrl}
          doiReservationCheck={this.doiReservationCheck}
        />
      );
    } else if (showChangeCommunityButton) {
      result = (
        <>
          <CommunitySelectionModal
            onCommunityChange={(community) => {
              changeSelectedCommunityFn(community);
            }}
            onModalChange={(value) => this.setState({ modalOpen: value })}
            handleClose={() => this.setState({ modalOpen: false })}
            modalOpen={modalOpen}
            displaySelected
            record={record}
            chosenCommunity={community}
            trigger={
              <Button content={i18next.t("Change community")} fluid className="mb-10" />
            }
          />
          <PublishButton
            buttonLabel={i18next.t("Publish without community")}
            doiReservationCheck={this.doiReservationCheck}
            publishWithoutCommunity
            gprUrl={gprUrl}
            {...ui}
          />
        </>
      );
    } else {
      result = (
        <PublishButton
          doiReservationCheck={this.doiReservationCheck}
          gprUrl={gprUrl}
          {...ui}
        />
      );
    }
    return result;
  }
}

SubmitReviewOrPublishComponent.propTypes = {
  community: PropTypes.object,
  changeSelectedCommunityFn: PropTypes.func.isRequired,
  showChangeCommunityButton: PropTypes.bool.isRequired,
  showDirectPublishButton: PropTypes.bool.isRequired,
  showSubmitForReviewButton: PropTypes.bool.isRequired,
  record: PropTypes.object.isRequired,
  raiseDOINeededButNotReserved: PropTypes.func.isRequired,
  gprUrl: PropTypes.string,
};

SubmitReviewOrPublishComponent.defaultProps = {
  community: undefined,
  gprUrl: null,
};

const mapStateToProps = (state) => ({
  community: state.deposit.editorState.selectedCommunity,
  showDirectPublishButton: state.deposit.editorState.ui.showDirectPublishButton,
  showChangeCommunityButton: state.deposit.editorState.ui.showChangeCommunityButton,
  showSubmitForReviewButton: state.deposit.editorState.ui.showSubmitForReviewButton,
});

const mapDispatchToProps = (dispatch) => ({
  changeSelectedCommunityFn: (community) =>
    dispatch(changeSelectedCommunity(community)),
  raiseDOINeededButNotReserved: (data, errors, errorType) =>
    dispatch({
      type: errorType,
      payload: { data: data, errors: errors },
    }),
});

export const SubmitReviewOrPublishButton = connect(
  mapStateToProps,
  mapDispatchToProps
)(SubmitReviewOrPublishComponent);
